// This tab's owner context: whose data it shows (owner key, incarnation,
// fence epoch), whether it is suspended by a transition, and the registry of
// async writers C3 step 2 stops. Every writer captures the context when it
// starts (spec 0.3) and passes it to fencedWrite, which re-checks it.
//
// No browser global at module scope.
import type { WriterCtx } from './offlineStore'

let ctx: WriterCtx | null = null
let suspended = false
const writers = new Set<AbortController>()
const listeners = new Set<() => void>()

function notify(): void {
  for (const l of [...listeners]) l()
}

export function subscribeOfflineContext(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** The context writers capture now (null: no owner store is active, e.g. guest or not loaded). */
export function currentWriterCtx(): WriterCtx | null {
  return suspended ? null : ctx
}

/** The context as known, even while suspended. */
export function knownWriterCtx(): WriterCtx | null {
  return ctx
}

export function setWriterCtx(next: WriterCtx | null): void {
  ctx = next
  notify()
}

export function isSuspended(): boolean {
  return suspended
}

export function setSuspended(value: boolean): void {
  if (suspended === value) return
  suspended = value
  if (value) stopAllWriters()
  notify()
}

/** Adopt a new fence epoch for the same owner (a cancelled transition). */
export function adoptFenceEpoch(epoch: number): void {
  if (!ctx) return
  ctx = { ...ctx, fenceEpoch: epoch }
  notify()
}

export interface WriterHandle {
  ctx: WriterCtx
  signal: AbortSignal
  end: () => void
}

/**
 * Register an async writer (a snapshot refresh, a draft save, a queue
 * drain). null when no owner context is active. The writer passes
 * `handle.ctx` to every fencedWrite and stops when `signal` aborts.
 */
export function beginWriter(): WriterHandle | null {
  const c = currentWriterCtx()
  if (!c) return null
  const ac = new AbortController()
  writers.add(ac)
  return { ctx: c, signal: ac.signal, end: () => { writers.delete(ac) } }
}

/** C3 step 2: signal every writer of this tab to stop. */
export function stopAllWriters(): void {
  for (const ac of [...writers]) ac.abort()
  writers.clear()
}

export function activeWriterCount(): number {
  return writers.size
}

/** Test seam. */
export function __resetOfflineContextForTests(): void {
  ctx = null
  suspended = false
  writers.clear()
  listeners.clear()
}
